/**
 * A read-only `.xlsx` reader, in the browser, with no dependencies.
 *
 * An xlsx file is a ZIP of XML. Both halves of that are things the platform
 * already does: `DecompressionStream('deflate-raw')` inflates a ZIP member and
 * `DOMParser` reads the XML. What is left is the ZIP container format, which is
 * about sixty lines of `DataView`.
 *
 * The alternative was SheetJS. The npm build is pinned at 0.18.5 with an
 * unpatched prototype-pollution advisory in `sheet_to_json`, and the patched
 * releases are published only to the vendor's own CDN — a supply chain this
 * repository does not need for "read a grid of cells".
 *
 * **What this does not do.** It reads the first worksheet, values only. No
 * formulas (the cached value is used), no styles, no merged cells, no multiple
 * sheets. In particular it does **not** resolve number formats, so a cell Excel
 * displays as `30/07/2026` arrives as the serial `46236`. `looksLikeExcelSerial`
 * and `fromExcelSerial` below are how the caller deals with that, and the bulk
 * importer applies them to its date column.
 */

// ── ZIP ──────────────────────────────────────────────────────────────────────

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;

interface ZipEntry {
  name: string;
  compression: number;
  compressedSize: number;
  localHeaderOffset: number;
}

/**
 * The central directory, read back to front.
 *
 * The end-of-central-directory record is last in the file but variable in
 * position, because it carries an optional comment of up to 64 KB. Scanning
 * backwards for its signature is the standard way in, and it is why a ZIP can be
 * appended to without rewriting it.
 */
function readCentralDirectory(view: DataView): ZipEntry[] {
  const limit = Math.max(0, view.byteLength - 0x10000 - 22);
  let eocd = -1;
  for (let i = view.byteLength - 22; i >= limit; i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) {
    throw new Error('Not a valid .xlsx file — no ZIP end-of-directory record found.');
  }

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];

  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_SIGNATURE) break;

    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);

    entries.push({
      compression: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      name: new TextDecoder().decode(
        new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength),
      ),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });

    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function readEntry(bytes: Uint8Array, view: DataView, entry: ZipEntry): Promise<string> {
  // The local header repeats the name and extra fields, and its extra field
  // length can differ from the central directory's — so the data offset has to
  // be computed from the local header, never from the central one.
  const local = entry.localHeaderOffset;
  const nameLength = view.getUint16(local + 26, true);
  const extraLength = view.getUint16(local + 28, true);
  const start = local + 30 + nameLength + extraLength;
  const raw = bytes.subarray(start, start + entry.compressedSize);

  // Method 0 is stored, method 8 is deflate. Nothing else appears in an xlsx.
  if (entry.compression === 0) return new TextDecoder().decode(raw);
  if (entry.compression !== 8) {
    throw new Error(`${entry.name} uses an unsupported ZIP compression method.`);
  }

  const stream = new Blob([raw as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

// ── Spreadsheet ──────────────────────────────────────────────────────────────

/** `"BC7"` → 54. Base-26 with no zero, which is why the accumulator starts at 0. */
function columnIndex(reference: string): number {
  let index = 0;
  for (const character of reference) {
    const code = character.charCodeAt(0);
    if (code < 65 || code > 90) break;
    index = index * 26 + (code - 64);
  }
  return index - 1;
}

/** All text under a node, so a rich-text run split across `<t>`s reads as one. */
function textOf(node: Element | null): string {
  if (!node) return '';
  return Array.from(node.getElementsByTagName('t'))
    .map((t) => t.textContent ?? '')
    .join('');
}

function parseXml(xml: string, what: string): Document {
  const document_ = new DOMParser().parseFromString(xml, 'application/xml');
  if (document_.getElementsByTagName('parsererror').length > 0) {
    throw new Error(`${what} inside the workbook is not valid XML.`);
  }
  return document_;
}

/**
 * Read the first worksheet as a rectangular grid of strings.
 *
 * Sparse rows and columns are filled with empty strings, so `rows[2][4]` is
 * always safe to index and a blank cell reads as `''` rather than `undefined`.
 */
export async function readXlsx(file: File | Blob): Promise<string[][]> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  const entries = readCentralDirectory(view);
  const find = (name: string) => entries.find((e) => e.name === name);

  // Which worksheet is first is stated in workbook.xml as a relationship id,
  // and resolved through the rels file. Picking the alphabetically first
  // `sheet*.xml` instead would silently open the wrong tab whenever the sheets
  // were reordered after creation.
  const workbookEntry = find('xl/workbook.xml');
  if (!workbookEntry) throw new Error('Not a valid .xlsx file — xl/workbook.xml is missing.');

  const workbook = parseXml(await readEntry(bytes, view, workbookEntry), 'xl/workbook.xml');
  const firstSheet = workbook.getElementsByTagName('sheet')[0];
  const relationshipId = firstSheet?.getAttribute('r:id') ?? firstSheet?.getAttribute('id') ?? null;

  let sheetPath = 'xl/worksheets/sheet1.xml';
  const relsEntry = find('xl/_rels/workbook.xml.rels');
  if (relationshipId && relsEntry) {
    const rels = parseXml(await readEntry(bytes, view, relsEntry), 'the workbook relationships');
    for (const relationship of Array.from(rels.getElementsByTagName('Relationship'))) {
      if (relationship.getAttribute('Id') !== relationshipId) continue;
      const target = relationship.getAttribute('Target') ?? '';
      sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
      break;
    }
  }

  const sheetEntry = find(sheetPath) ?? find('xl/worksheets/sheet1.xml');
  if (!sheetEntry) throw new Error('The workbook has no readable worksheet.');

  // Shared strings are optional: a sheet whose every cell is inline or numeric
  // has no such part at all.
  let shared: string[] = [];
  const sharedEntry = find('xl/sharedStrings.xml');
  if (sharedEntry) {
    const document_ = parseXml(await readEntry(bytes, view, sharedEntry), 'the shared strings');
    shared = Array.from(document_.getElementsByTagName('si')).map((si) => textOf(si));
  }

  const sheet = parseXml(await readEntry(bytes, view, sheetEntry), 'the worksheet');
  const rows: string[][] = [];
  let widest = 0;

  for (const row of Array.from(sheet.getElementsByTagName('row'))) {
    const cells: string[] = [];

    for (const cell of Array.from(row.getElementsByTagName('c'))) {
      const reference = cell.getAttribute('r') ?? '';
      const index = reference ? columnIndex(reference) : cells.length;
      const type = cell.getAttribute('t');

      let value: string;
      if (type === 's') {
        const at = Number(cell.getElementsByTagName('v')[0]?.textContent ?? '-1');
        value = shared[at] ?? '';
      } else if (type === 'inlineStr') {
        value = textOf(cell.getElementsByTagName('is')[0] ?? null);
      } else {
        // `str` is a formula returning text; everything else is a number or a
        // boolean, and `<v>` holds the cached result either way.
        value = cell.getElementsByTagName('v')[0]?.textContent ?? '';
      }

      while (cells.length < index) cells.push('');
      cells[index] = value.trim();
    }

    widest = Math.max(widest, cells.length);
    rows.push(cells);
  }

  for (const row of rows) {
    while (row.length < widest) row.push('');
  }

  return rows;
}

// ── Excel's dates ────────────────────────────────────────────────────────────

/**
 * Does this look like a date Excel stored as a serial number?
 *
 * Only bare integers in a plausible range qualify. 20000 is 1954 and 60000 is
 * 2064, which brackets every date an accounting system will legitimately see
 * while excluding the amounts and quantities that share the column type.
 */
export function looksLikeExcelSerial(value: string): boolean {
  if (!/^\d{5}$/.test(value)) return false;
  const serial = Number(value);
  return serial >= 20000 && serial <= 60000;
}

/**
 * Excel serial → `yyyy-mm-dd`.
 *
 * The epoch is 1899-12-30, not 1899-12-31, because Excel believes 1900 was a
 * leap year. It was not — the bug is inherited from Lotus 1-2-3 and preserved
 * deliberately for compatibility. Shifting the epoch back one day cancels it for
 * every date after February 1900, which is every date this will ever be given.
 */
export function fromExcelSerial(serial: number): string {
  const date = new Date(Date.UTC(1899, 11, 30) + serial * 86_400_000);
  return date.toISOString().slice(0, 10);
}
